import { getIconPack, getIconUrl, getPackSize } from "@nova/component-map-icons"
import classNames from "classnames"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { selectActivatedFireId } from "features/map/control/selectors"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { setSelectedTool } from "plugins/Toolbox/actions"
import { ToolHoverLabel } from "plugins/Toolbox/Shared/ToolHoverLabel"
import { setIconIsBeingDragged } from "plugins/Toolbox/tool-registry"
import { MouseEvent, useCallback, useEffect, useState } from "react"
import { defaultIconColor, defaultIconSize } from "./constants"
import { ContextMenuPosition } from "features/map/types"
import { Icon } from "@nova/protobuf"
import { decodeNumberToColor } from "@nova/serializer/src/client/util"
import { updateIcon } from "features/pubsub/entityActions/cubes/icon"
import { navbarHeight, getRotatedIconsHardCodedValues, ToolName, LineWidth } from "@nova/constants"
import { selectActiveIconId } from "plugins/Toolbox/Icons/selectors"
import { selectSingleLayer } from "panels/content/side-panel/slice.new"
import { Marker, MarkerDragEvent } from "react-map-gl"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"

export type MarkerData = {
    url: string
    color?: string
    stroke?: string
    size?: number
    viewbox?: string
    outline?: number
    width?: number
}
type MapboxMarkerIconProps = {
    icon: Icon
    contextMenuOpen?: boolean
    setContextMenuPosition?: (position?: ContextMenuPosition) => void
    isDisabled?: boolean
}
export const MapboxMarkerIcon = ({
    icon,
    contextMenuOpen,
    setContextMenuPosition,
    isDisabled,
}: MapboxMarkerIconProps) => {
    const dispatch = useAppDispatch()
    const { isEditor } = useWorkspacePermissions()

    const [markerData, setMarkerData] = useState<MarkerData>()
    const [isHovering, setIsHovering] = useState(false)
    const activeIconId = useAppSelector(selectActiveIconId)
    const fireId = useAppSelector(selectActivatedFireId)

    useEffect(() => {
        const key = icon.url
            .replace("/images/icons/wildfire/basic", "fireai_wildfire")
            .replace(".svg", "")
        const pack = getIconPack(key)
        //second call is for backwards compatibility
        const iconUrl = getIconUrl(key) || getIconUrl("fireai_" + key)
        if (!iconUrl) {
            return
        }
        const color =
            pack === "shape" || pack === "numbers"
                ? icon.color
                    ? decodeNumberToColor(icon.color)
                    : defaultIconColor
                : undefined
        const stroke =
            pack === "shape"
                ? "white"
                : pack === "numbers"
                  ? decodeNumberToColor(icon.outline || 0xff)
                  : undefined
        const size = getPackSize(pack) || defaultIconSize
        const viewbox = pack === "shape" ? "0 0 24 25" : "0 0 32 33"
        setMarkerData({
            url: iconUrl,
            color,
            stroke,
            size,
            viewbox,
            width: icon.width === LineWidth.thick ? 1 : icon.width === LineWidth.normal ? 0.5 : 0,
        })
    }, [icon])

    const onClick: React.ComponentProps<typeof Marker>["onClick"] = (event) => {
        if (!icon || !isEditor || isDisabled) {
            return false
        }
        dispatch(selectSingleLayer({ id: icon.id }))
        event.originalEvent.stopPropagation()
    }

    const onDragStart = useCallback(() => {
        if (contextMenuOpen || isDisabled) return
        dispatch(setIconIsBeingDragged(true))
    }, [contextMenuOpen, isDisabled, dispatch])

    const onDragEnd = useCallback(
        (event: MarkerDragEvent) => {
            dispatch(setIconIsBeingDragged(false))

            if (!fireId) return
            const { lng, lat } = event.lngLat
            dispatch(
                updateIcon({
                    id: icon.id,
                    updates: {
                        longitude: lng,
                        latitude: lat,
                    },
                })
            )
            dispatch(
                setSelectedTool({
                    toolName: ToolName.Icon,
                    tool: icon,
                })
            )
        },
        [icon, fireId, dispatch]
    )

    const sidePanelWidth = useResizableSidePanelWidth()
    const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
        if (isDisabled) return

        event.preventDefault()
        dispatch(setSelectedTool({ toolName: ToolName.Icon, tool: icon }))
        setContextMenuPosition?.({
            top: event.clientY - navbarHeight,
            left: event.clientX - sidePanelWidth,
            layer: "map-icons",
            isMap: false,
        })
        event.stopPropagation()
    }

    if (!markerData) {
        return null
    }
    const { url, color, stroke, size, viewbox, width } = markerData
    return (
        <>
            <Marker
                longitude={icon.longitude}
                latitude={icon.latitude}
                draggable={icon.id === activeIconId && !isDisabled}
                className={classNames({
                    "cursor-default": !isEditor,
                })}
                style={{ pointerEvents: isDisabled ? "none" : "auto" }} // This doesn't work as a conditional Tailwind class.
                onClick={onClick}
                data-iconid={icon.id}
                onDragStart={onDragStart}
                onDragEnd={onDragEnd}
                rotation={getRotatedIconsHardCodedValues(icon.url)}
            >
                <div
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    onContextMenu={handleContextMenu}
                    className={classNames(
                        icon.id === activeIconId && "border-[2px] rounded-md p-2"
                    )}
                    style={{ borderColor: color ?? "#E34752" }}
                >
                    <svg
                        data-src={url}
                        height={size}
                        width={size}
                        style={{ fill: color }}
                        stroke={stroke}
                        strokeWidth={width}
                        viewBox={viewbox}
                    />
                </div>
            </Marker>
            {isHovering && activeIconId !== icon.id && !isDisabled && (
                <ToolHoverLabel label={icon.name} />
            )}
        </>
    )
}
